import { useState, useEffect, useCallback } from 'react';
import { dashboardAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { getActivityIcon } from '@/lib/activity-types';
import {
  Users, Building2, TrendingUp, Clock, AlertTriangle,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

function KPICard({ title, value, subtitle, icon: Icon, trend, color = 'primary', onClick }) {
  return (
    <Card
      className="kpi-card cursor-pointer border-border/60 hover:border-primary/30"
      onClick={onClick}
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-3 sm:p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1 sm:space-y-2">
            <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-xl sm:text-3xl font-bold tracking-tight">{value}</p>
            {subtitle && (
              <p className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-1 hidden sm:flex">
                {trend > 0 ? <ArrowUpRight className="w-3 h-3 text-emerald-500" /> :
                 trend < 0 ? <ArrowDownRight className="w-3 h-3 text-red-500" /> : null}
                {subtitle}
              </p>
            )}
          </div>
          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center`}
               style={{ backgroundColor: `hsl(var(--${color}) / 0.1)` }}>
            <Icon className="w-5 h-5" style={{ color: `hsl(var(--${color}))` }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const RANGE_OPTIONS = [
  { value: 'today', label: 'Vandaag' },
  { value: '7d', label: '7 Dagen' },
  { value: '30d', label: '30 Dagen' },
  { value: '90d', label: '90 Dagen' },
  { value: 'all', label: 'Alles' },
];

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('7d');
  const { user } = useAuth();
  const { stageMap, formatCurrency, formatDate } = useSettings();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const res = await dashboardAPI.get({ time_range: range });
      setData(res.data);
    } catch (err) {
      console.error('Dashboard fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return (
      <div className="space-y-6 fade-in" data-testid="dashboard-loading">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const stageData = Object.entries(data.stage_counts).map(([key, value]) => ({
    name: stageMap[key]?.label || key.charAt(0).toUpperCase() + key.slice(1),
    value,
    fill: stageMap[key]?.color || '#6B7280'
  }));

  const sourceData = (data.lead_sources || []).map((s, i) => ({
    name: s.source?.replace(/_/g, ' '),
    count: s.count,
    fill: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'][i % 7]
  }));

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
            Welkom terug, {user?.name?.split(' ')[0]}.{' '}
            <span className="hidden sm:inline">
              {data.user_role === 'agent' || !['admin', 'manager'].includes(data.user_role) ? 'Hier is je persoonlijk overzicht.' :
               data.user_role === 'manager' ? 'Hier is het teamoverzicht.' :
               'Hier is het bedrijfsoverzicht.'}
            </span>
          </p>
        </div>
        {data.user_role && (
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full flex-shrink-0 ${
            data.user_role === 'admin' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
            data.user_role === 'manager' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
            'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
          }`}>
            {data.user_role_name || user?.role_name || data.user_role}
          </span>
        )}
      </div>

      {/* Time Range Filter */}
      <div className="flex items-center gap-1.5 bg-muted/50 rounded-lg p-1 w-fit" data-testid="range-filter">
        {RANGE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => { setLoading(true); setRange(opt.value); }}
            data-testid={`range-${opt.value}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              range === opt.value
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KPICard
          title="Totaal Leads"
          value={data.total_leads}
          subtitle={`${data.new_in_range || 0} nieuw in periode`}
          icon={Users}
          trend={(data.new_in_range || 0) > 0 ? 1 : 0}
          onClick={() => navigate('/leads')}
        />
        <KPICard
          title="Conversieratio"
          value={`${data.conversion_rate}%`}
          subtitle="Gewonnen vs afgesloten"
          icon={TrendingUp}
          trend={data.conversion_rate > 40 ? 1 : -1}
          color="chart-2"
        />
        <KPICard
          title="Diensten"
          value={data.total_properties}
          subtitle={`${data.available_properties} beschikbaar`}
          icon={Building2}
          onClick={() => navigate('/properties')}
        />
        <KPICard
          title="Verlopen Opvolgingen"
          value={data.overdue_follow_ups}
          subtitle={`${data.today_follow_ups} vandaag`}
          icon={data.overdue_follow_ups > 0 ? AlertTriangle : Clock}
          trend={data.overdue_follow_ups > 0 ? -1 : 1}
          color={data.overdue_follow_ups > 0 ? 'destructive' : 'chart-2'}
          onClick={() => navigate('/follow-ups')}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
        <Card className="lg:col-span-5 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Pipeline Overzicht</CardTitle>
          </CardHeader>
          <CardContent>
            <PipelineFunnel stages={stageData} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-4 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Nieuwe Leads Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.trend_data}>
                <defs>
                  <linearGradient id="leadGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} interval={Math.max(0, Math.floor((data.trend_data?.length || 7) / 7) - 1)} />
                <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} />
                <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                <Area type="monotone" dataKey="leads" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#leadGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Leadbronnen</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={sourceData} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2}>
                  {sourceData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-1 mt-2">
              {sourceData.slice(0, 4).map((s) => (
                <div key={s.name} className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ background: s.fill }} />
                  <span className="text-[10px] text-muted-foreground capitalize truncate">{s.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="border-border/60">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Recente Activiteit</CardTitle>
            <button
              onClick={() => navigate('/activities')}
              className="text-xs text-primary hover:underline font-medium"
              data-testid="view-all-activities"
            >
              Alles bekijken
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(data.recent_activities || []).map((act) => {
              const Icon = getActivityIcon(act.type);
              return (
                <div key={act.id} className="flex items-start gap-3 py-2 border-b border-border/40 last:border-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">{act.created_by_name}</span>
                      <span className="text-muted-foreground"> heeft een {act.type} gelogd met </span>
                      <span className="font-medium">{act.lead_name}</span>
                    </p>
                    {act.notes && <p className="text-xs text-muted-foreground mt-0.5 truncate">{act.notes}</p>}
                  </div>
                  <span className="text-[10px] text-muted-foreground mono flex-shrink-0">
                    {formatDate(act.created_at)}
                  </span>
                </div>
              );
            })}
            {(!data.recent_activities || data.recent_activities.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-8">Geen recente activiteit</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function PipelineFunnel({ stages }) {
  if (!stages || stages.length === 0) return <p className="text-xs text-muted-foreground text-center py-8">Geen pipeline data</p>;

  const maxValue = Math.max(...stages.map(s => s.value), 1);

  return (
    <div className="space-y-1.5 py-2">
      {stages.map((stage) => {
        const widthPct = Math.max(20, (stage.value / maxValue) * 100);
        return (
          <div key={stage.name} className="flex items-center gap-3 group">
            <div className="w-24 text-right flex-shrink-0">
              <span className="text-[11px] text-muted-foreground">{stage.name}</span>
            </div>
            <div className="flex-1 relative">
              <div
                className="h-8 rounded-md flex items-center justify-between px-3 transition-all duration-500 group-hover:opacity-90"
                style={{
                  width: `${widthPct}%`,
                  backgroundColor: stage.fill || '#6B7280',
                  minWidth: '40px',
                }}
              >
                <span className="text-[11px] font-semibold text-white">{stage.value}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
